import { useCallback, useEffect, useRef, useState } from 'react';
import { playResultStepSound } from '../services/soundEffects';

// The browser's actual animation start, not a second timer, cues each note.
export const resultMetricDelay = index => 140 + index * 130;
export const resultRecordDelay = (index, metricCount = 4) => resultMetricDelay(metricCount) + index * 40;
export const resultRevealStyle = delay => ({ '--result-reveal-delay': `${delay}ms` });

export default function useResultEntrance(enabled = true, resultKey, paused = false) {
  const played = useRef(new Set());
  const controller = useRef(null);
  const [interrupted, setInterrupted] = useState(() => Boolean(document.hidden));

  useEffect(() => {
    played.current.clear();
    setInterrupted(Boolean(document.hidden));
    const interrupt = () => {
      if (!document.hidden) return;
      controller.current?.abort();
      // Returning to the tab shows the finished result, never a burst of notes.
      setInterrupted(true);
    };
    document.addEventListener('visibilitychange', interrupt);
    return () => {
      document.removeEventListener('visibilitychange', interrupt);
    };
  }, [enabled, resultKey]);

  useEffect(() => {
    controller.current = new AbortController();
    return () => controller.current?.abort();
  }, [enabled, resultKey, paused]);

  const onAnimationStart = useCallback(event => {
    if (!enabled || paused || interrupted || document.hidden || controller.current?.signal.aborted) return;
    if (!['resultRevealIn', 'resultRevealSoft'].includes(event.animationName)) return;
    const value = event.target.getAttribute('data-result-tone');
    if (value === null || played.current.has(value)) return;
    played.current.add(value);
    const totalSteps = event.currentTarget.querySelectorAll('[data-result-tone]').length;
    playResultStepSound(Number(value), { signal: controller.current?.signal, totalSteps });
  }, [enabled, interrupted, paused]);

  return { className: enabled && !interrupted ? `result-entrance${paused ? ' is-result-paused' : ''}` : '', onAnimationStart };
}
